import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { useNotifications } from "@/context/NotificationContext";
import { hasModule, hasSpecialPermission } from "@/lib/permissions";
import api from "@/lib/api";
import { fmtDate } from "@/components/shared";
import {
  SquaresFour,
  ListChecks,
  UsersThree,
  Database,
  ClockCountdown,
  ChartBar,
  ShieldCheck,
  Bell,
  SignOut,
  List as ListIcon,
  MagnifyingGlass,
  Plus,
  FlowArrow,
  Package,
  Storefront,
  Receipt,
  Gear,
  AddressBook,
  ClipboardText,
  ArrowsOut,
  ArrowsIn,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  {
    section: "Overview",
    items: [{ to: "/", label: "Dashboard", icon: SquaresFour }],
  },
  {
    section: "Work",
    items: [
      { to: "/tasks", label: "Tasks", icon: ListChecks, module: "tasks" },
      {
        to: "/extensions",
        label: "Extension Requests",
        icon: ClockCountdown,
        module: "extensions",
      },
    ],
  },
  {
    section: "Production",
    items: [
      {
        to: "/orders",
        label: "Jobs Tracking Sheet",
        icon: Package,
        module: "orders",
      },
      {
        to: "/workflows",
        label: "Workflow Templates",
        icon: FlowArrow,
        module: "workflows",
      },
    ],
  },
  {
    section: "Procurement",
    items: [
      {
        to: "/purchase-orders",
        label: "Purchase Orders",
        icon: Receipt,
        module: "vendors",
      },
      {
        to: "/vendors",
        label: "Vendor Registry",
        icon: Storefront,
        module: "vendors",
      },
      {
        to: "/material-receipts",
        label: "Material Receipts",
        icon: ClipboardText,
        module: "vendors",
        special: "store_receipts",
      },
    ],
  },
  {
    section: "Directory",
    items: [
      { to: "/team", label: "Team Members", icon: UsersThree, module: "team" },
      {
        to: "/customers",
        label: "Customers",
        icon: AddressBook,
        module: "customers",
      },
    ],
  },
  {
    section: "Insights",
    items: [
      { to: "/reports", label: "Reports", icon: ChartBar, module: "reports" },
      { to: "/audit", label: "Audit Log", icon: ShieldCheck, module: "audit" },
    ],
  },
  {
    section: "Admin",
    items: [
      { to: "/access-requests", label: "Access Requests", icon: Bell },
      {
        to: "/permissions",
        label: "Permissions",
        icon: ShieldCheck,
        adminOnly: true,
      },
      { to: "/masters", label: "Masters", icon: Database, module: "masters" },
      {
        to: "/settings",
        label: "Company Settings",
        icon: Gear,
        adminOnly: true,
      },
    ],
  },
];

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);
  return now;
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { focusMode, setFocusMode } = useUI();
  const { unread, pendingAccess, poPlaceAlerts, refresh } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const now = useClock();

  // The shared context already polls every 30s; also nudge it on every route change so
  // the badges feel current right after an admin grants/denies from another page.
  useEffect(() => {
    refresh();
  }, [location.pathname, refresh]);

  // Full Screen (available on every page): native Fullscreen API + hides the sidebar via focusMode.
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
      setFocusMode(true);
    } else {
      document.exitFullscreen?.();
      setFocusMode(false);
    }
  };
  useEffect(() => {
    const onChange = () => {
      if (!document.fullscreenElement) setFocusMode(false);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, [setFocusMode]);
  // Leaving focus mode should never strand the user in native fullscreen with no way back.
  useEffect(() => {
    if (!focusMode && document.fullscreenElement) document.exitFullscreen?.();
  }, [focusMode]);

  const roleLabel = {
    admin: "Business Owner",
    manager: "Manager",
    member: "Team Member",
  }[user?.role];

  const currentLabel = (() => {
    let best = null;
    for (const grp of NAV)
      for (const i of grp.items) {
        if (
          location.pathname === i.to ||
          (i.to !== "/" && location.pathname.startsWith(i.to))
        ) {
          if (!best || i.to.length > best.to.length) best = i;
        }
      }
    return best?.label || "Portal";
  })();

  const doSearch = (e) => {
    e.preventDefault();
    if (search.trim())
      navigate(`/tasks?search=${encodeURIComponent(search.trim())}`);
  };

  const loadRecentNotifications = () => {
    api
      .get("/notifications")
      .then((response) => setRecentNotifications(response.data.slice(0, 6)))
      .catch(() => setRecentNotifications([]));
  };

  const handleNotificationsOpen = (nextOpen) => {
    setNotificationsOpen(nextOpen);
    if (nextOpen) loadRecentNotifications();
  };

  const openNotification = async (notification) => {
    try {
      await api.post(`/notifications/${notification.id}/read`);
    } catch {
      // Navigation should remain available if a notification was already resolved elsewhere.
    }

    setRecentNotifications((items) =>
      items.map((item) =>
        item.id === notification.id ? { ...item, read: true } : item,
      ),
    );
    refresh();
    setNotificationsOpen(false);

    if (
      notification.meta?.type === "order_missing_fields" &&
      notification.meta?.order_id
    ) {
      const field = notification.meta.focus_field;
      navigate(
        `/orders/${notification.meta.order_id}?edit=1${field ? `&focus=${field}` : ""}`,
      );
      return;
    }

    if (notification.task_id) {
      navigate(`/tasks/${notification.task_id}`);
      return;
    }

    navigate("/notifications");
  };

  const Sidebar = (
    <aside className="flex h-full w-64 shrink-0 flex-col border-r border-emerald-900/50 bg-[#08281b] text-slate-100 lg:w-72">
      <div className="border-b border-emerald-900/50 bg-[#061f15] p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <img
              src="/dvepl-logo.svg"
              alt="DVEPL logo"
              className="h-10 w-10 shrink-0 rounded-xl border border-emerald-400/30 shadow-md"
              data-testid="sidebar-logo"
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-heading text-base font-extrabold tracking-tight text-white">
                  DVEPL
                </span>
                <span className="rounded border border-emerald-400/30 bg-emerald-500/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-200">
                  Panel Hub
                </span>
              </div>
              <p className="mt-0.5 text-[10px] font-medium text-emerald-100/60">
                D.V. Engineers Pvt. Ltd.
              </p>
            </div>
          </div>
          <span
            className="led-dot led-pulse shrink-0 bg-emerald-400 text-emerald-400"
            title="Systems online"
            data-testid="sidebar-status-led"
          />
        </div>
        <div className="mt-4 flex gap-2 border-t border-emerald-900/50 pt-3">
          <button
            type="button"
            onClick={() => navigate("/orders")}
            data-testid="sidebar-add-job"
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-bold text-white shadow-sm transition-colors hover:bg-emerald-500"
          >
            <Plus size={15} weight="bold" /> Add New Job
          </button>
          <button
            type="button"
            onClick={() => navigate("/notifications")}
            data-testid="sidebar-reminders-shortcut"
            className="relative flex items-center justify-center rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-3 text-emerald-200 transition-colors hover:bg-emerald-500/20"
            aria-label="Open reminders"
          >
            <Bell size={16} />
            {unread > 0 && (
              <span className="absolute -right-1.5 -top-1.5 rounded-full bg-amber-400 px-1.5 py-0.5 text-[9px] font-black text-emerald-950">
                {unread}
              </span>
            )}
          </button>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {NAV.map((grp) => {
          const items = grp.items.filter((i) =>
            i.adminOnly
              ? user?.role === "admin"
              : hasModule(user, i.module) ||
                (i.special && hasSpecialPermission(user, i.special)),
          );
          if (!items.length) return null;
          return (
            <div key={grp.section} className="mb-5">
              <div className="mb-2 flex items-center justify-between px-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-100/45">
                  {grp.section}
                </span>
                {grp.section === "Production" && (
                  <span className="text-[9px] font-semibold text-emerald-300">
                    ACTIVE VIEW
                  </span>
                )}
              </div>
              {items.map((i) => {
                const active =
                  location.pathname === i.to ||
                  (i.to !== "/" && location.pathname.startsWith(i.to));
                const Icon = i.icon;
                return (
                  <Link
                    key={i.to}
                    to={i.to}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`mb-1 flex items-center justify-between rounded-xl border px-3 py-2.5 text-left transition-colors duration-150 ${
                      active
                        ? "border-emerald-400/50 bg-emerald-600/20 text-white shadow-sm"
                        : "border-transparent text-emerald-50/75 hover:bg-emerald-950/60 hover:text-white"
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                          active
                            ? "bg-emerald-500 text-white shadow-sm"
                            : "bg-emerald-950/70 text-emerald-100/55"
                        }`}
                      >
                        <Icon size={16} weight={active ? "fill" : "regular"} />
                      </span>
                      <span className="min-w-0">
                        <span
                          className={`block text-xs font-bold ${active ? "text-emerald-200" : "text-emerald-50"}`}
                        >
                          {i.label}
                        </span>
                        <span className="mt-0.5 block truncate text-[10px] text-emerald-100/45">
                          {i.sectionHint || grp.section}
                        </span>
                      </span>
                    </span>
                    {i.to === "/purchase-orders" && poPlaceAlerts > 0 && (
                      <span
                        className="w-2 h-2 bg-red-500 rounded-full shrink-0 animate-pulse"
                        title={`${poPlaceAlerts} PO(s) cleared and ready to place with the vendor`}
                        data-testid="po-place-alert-nav-dot"
                      />
                    )}
                    {i.to === "/access-requests" && pendingAccess > 0 && (
                      <span
                        className="min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shrink-0"
                        data-testid="access-requests-nav-badge"
                      >
                        {pendingAccess}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>
      <div className="space-y-2.5 border-t border-emerald-900/50 bg-[#061f15] p-3.5">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full border border-emerald-400/30 bg-emerald-700 font-heading font-bold text-white">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="truncate text-xs font-bold text-white">
              {user?.name}
            </div>
            <div className="truncate text-[10px] font-semibold text-emerald-300">
              {roleLabel}
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="text-emerald-100/55 transition-colors hover:text-white"
          >
            <SignOut size={18} />
          </button>
        </div>
        <div
          className="text-right font-mono text-[9px] text-emerald-100/35"
          data-testid="app-version"
        >
          v25
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#f4f7f4] text-slate-900">
      {!focusMode && (
        <div className="fixed inset-y-0 left-0 z-40 hidden w-64 md:block lg:w-72">
          {Sidebar}
        </div>
      )}
      {!focusMode && open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}
      <div
        className={`flex min-h-screen min-w-0 flex-col ${focusMode ? "" : "md:ml-64 lg:ml-72"}`}
      >
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-4 shadow-sm lg:px-6">
          <button
            className="text-emerald-800 md:hidden"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <ListIcon size={22} />
          </button>
          <div
            className="hidden items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500 md:flex"
            data-testid="header-breadcrumb"
          >
            <span className="text-slate-400">SYS //</span>
            <span className="text-emerald-700">{currentLabel}</span>
          </div>
          <form onSubmit={doSearch} className="flex-1 max-w-md relative">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tasks, IDs, tags…"
              data-testid="global-search"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 transition-colors focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600"
            />
          </form>
          <div className="flex items-center gap-3 ml-auto">
            <div
              className="hidden items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 xl:flex"
              data-testid="header-clock"
            >
              <span className="led-dot led-pulse text-emerald-400 bg-emerald-400" />
              <span className="font-mono text-xs tabular-nums text-slate-700">
                {now.toLocaleTimeString("en-GB", { hour12: false })}
              </span>
              <span className="font-mono text-[10px] uppercase text-slate-400">
                {now.toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                })}
              </span>
            </div>
            <Button
              onClick={toggleFullscreen}
              variant="outline"
              size="sm"
              data-testid="toggle-fullscreen-btn"
              title={focusMode ? "Exit Full Screen" : "Full Screen"}
              className="gap-1.5 rounded-lg border-slate-200 bg-white text-slate-600 hover:border-emerald-400 hover:text-emerald-700"
            >
              {focusMode ? <ArrowsIn size={15} /> : <ArrowsOut size={15} />}
              <span className="hidden sm:inline">
                {focusMode ? "Exit Full Screen" : "Full Screen"}
              </span>
            </Button>
            <Button
              onClick={() => navigate("/tasks/new")}
              data-testid="header-new-task"
              className="hidden gap-1.5 rounded-lg bg-emerald-600 font-semibold text-white hover:bg-emerald-700 sm:flex"
            >
              <Plus weight="bold" size={16} /> New Task
            </Button>
            <DropdownMenu
              open={notificationsOpen}
              onOpenChange={handleNotificationsOpen}
            >
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="notifications-bell"
                  aria-label="Open recent reminders"
                  className="relative p-2 text-slate-500 transition-colors hover:text-emerald-700"
                >
                  <Bell size={20} />
                  {unread > 0 && (
                    <span
                      data-testid="notifications-unread-count"
                      className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-emerald-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
                    >
                      {unread}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-[min(24rem,calc(100vw-2rem))] border-slate-200 bg-white p-1.5 text-slate-900 shadow-2xl"
                data-testid="notifications-bell-popover"
              >
                <DropdownMenuLabel
                  className="flex items-center justify-between px-2.5 py-2"
                  data-testid="notifications-popover-title"
                >
                  <span className="font-heading text-sm text-slate-900">
                    Recent reminders
                  </span>
                  {unread > 0 && (
                    <span
                      className="text-[10px] font-mono uppercase tracking-wider text-emerald-400"
                      data-testid="notifications-popover-unread"
                    >
                      {unread} unread
                    </span>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-slate-200" />
                <div className="max-h-80 overflow-y-auto py-1">
                  {recentNotifications.length === 0 && (
                    <div
                      className="px-2.5 py-5 text-center text-xs text-slate-500"
                      data-testid="notifications-popover-empty"
                    >
                      No reminders right now.
                    </div>
                  )}
                  {recentNotifications.map((notification) => (
                    <DropdownMenuItem
                      key={notification.id}
                      onSelect={(event) => {
                        event.preventDefault();
                        openNotification(notification);
                      }}
                      data-testid={`notifications-popover-item-${notification.id}`}
                      className="items-start gap-2.5 rounded-lg px-2.5 py-2.5 focus:bg-emerald-50"
                    >
                      <span
                        className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                          notification.read ? "bg-slate-300" : "bg-emerald-500"
                        }`}
                        data-testid={`notifications-popover-state-${notification.id}`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-semibold text-slate-900">
                          {notification.title}
                        </span>
                        <span className="mt-0.5 block line-clamp-2 text-[11px] leading-relaxed text-slate-500">
                          {notification.message}
                        </span>
                        <span
                          className="mt-1 block font-mono text-[10px] text-slate-400"
                          data-testid={`notifications-popover-date-${notification.id}`}
                        >
                          {fmtDate(notification.at)}
                        </span>
                      </span>
                    </DropdownMenuItem>
                  ))}
                </div>
                <DropdownMenuSeparator className="bg-slate-200" />
                <DropdownMenuItem
                  onSelect={() => navigate("/notifications")}
                  data-testid="notifications-view-all"
                  className="justify-center rounded-sm py-2 text-xs font-semibold text-emerald-400 focus:bg-emerald-500/10"
                >
                  View all notifications
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto control-surface">
          <div className="min-h-full bg-[#f4f7f4]">{children}</div>
        </main>
      </div>
    </div>
  );
}
